import React, { useState, useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Paper, makeStyles,
} from '@material-ui/core';
import ProcedureNumberCell from './ProcedureNumberCell';
import { EQUIPMENT_DATA, mergeTabData, fmtDate } from './manufacturingTabData';
import Pagination from '../../logisticsDashboard/components/Pagination';


const useStyles = makeStyles(() => ({
  tableContainer: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    margin: '8px 0',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  scrollArea: {
    width: '100%',
    maxHeight: 440,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  headerRow: {
    backgroundColor: '#F9F9FC',
    '& th': {
      backgroundColor: '#F9F9FC',
      fontWeight: 600,
      fontSize: '0.88rem',
      color: '#333',
      padding: '8px 12px',
      borderBottom: '1px solid #e0e0e0',
      verticalAlign: 'bottom',
      lineHeight: 1.3,
      whiteSpace: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'normal',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },
  bodyRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
    '& td': {
      fontSize: '0.9rem',
      padding: '8px 12px',
      borderBottom: '1px solid #eeeeee',
      color: '#444',
      verticalAlign: 'top',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  dateTime: {
    display: 'block',
    lineHeight: 1.4,
  },
}));

// Each column takes the API value where present, otherwise falls back to the
// "Equipment" sheet of the batch's Manufacturing Collaboration workbook
// (see mergeTabData). Procedure Number is "Unit Procedure.Operation.Phase".
const EquipmentTab = ({ data, loading, batchNumber, pageSize = 10 }) => {
  const classes = useStyles();
  const PAGE_SIZE = pageSize || 10;
  const rows = mergeTabData(data, EQUIPMENT_DATA, batchNumber);

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [rows, safePage, PAGE_SIZE]
  );

  if (loading) return <Box style={{ textAlign: 'center', padding: 24 }}>Loading...</Box>;

  return (
    <Paper className={classes.tableContainer} elevation={0}>
      <div className={classes.scrollArea}>
        <Table stickyHeader size="small" style={{ width: '100%', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow className={classes.headerRow}>
              <TableCell>Phase / Step Number</TableCell>
              <TableCell>Equipment ID</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Equipment Type</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Actual<br />Start Date</TableCell>
              <TableCell>Actual<br />End Date</TableCell>
              <TableCell>Reservation ID</TableCell>
              <TableCell>Request Form</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} style={{ textAlign: 'center' }}>No data available</TableCell></TableRow>
            ) : pageRows.map((row, idx) => (
              <TableRow key={idx} className={classes.bodyRow}>
                <TableCell><ProcedureNumberCell value={row.procedureNumber} batchNumber={batchNumber} /></TableCell>
                <TableCell>{row.equipmentId}</TableCell>
                <TableCell>{row.location}</TableCell>
                <TableCell>{row.equipmentType}</TableCell>
                <TableCell>{row.description}</TableCell>
                <TableCell>{fmtDate(row.actualStartDate)}</TableCell>
                <TableCell>{fmtDate(row.actualEndDate)}</TableCell>
                <TableCell>{row.reservationId}</TableCell>
                <TableCell>
                  <span style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}>
                    Click To View
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {rows.length > PAGE_SIZE && (
        <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
      )}
    </Paper>
  );
};

export default EquipmentTab;
